"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EtherMirrorClient = void 0;
const ethers_1 = require("ethers");
const EtherERC721Client_1 = require("./EtherERC721Client");
const typechain_1 = require("../../../contracts/typechain");
const config_1 = require("../config");
const preOrderList_json_1 = __importDefault(require("../preOrderList.json"));
const preOrderList = preOrderList_json_1.default;
class EtherMirrorClient extends EtherERC721Client_1.EtherERC721Client {
    async connectProvider(address, provider) {
        const network = await provider.getNetwork();
        if (network.name == "homestead") {
            if (config_1.DeploymentInfo["mainnet"].mirror.proxyAddress != address) {
                return Promise.reject("contract address mismatches the network.");
            }
        }
        else if (network.name == "rinkeby") {
            if (config_1.DeploymentInfo["rinkeby"].mirror.proxyAddress != address) {
                return Promise.reject("contract address mismatches the network.");
            }
        }
        else {
            return Promise.reject("no contract address matches the network.");
        }
        this.mirror = typechain_1.MirrorImpl__factory.connect(address, provider);
        return super.connectProvider(address, provider);
    }
    connectSigner(signer) {
        return super.connectSigner(signer);
    }
    contract() {
        if (this.provider === undefined || this.mirror === undefined) {
            return Promise.reject("need to connect a valid provider");
        }
        return Promise.resolve(this.mirror);
    }
    stockSupply(config) {
        if (this.provider === undefined || this.mirror === undefined) {
            return Promise.reject("need to connect a valid provider");
        }
        return this.mirror.stockSupply({ ...config });
    }
    tokenPrice(config) {
        if (this.provider === undefined || this.mirror === undefined) {
            return Promise.reject("need to connect a valid provider");
        }
        return this.mirror.tokenPrice({ ...config });
    }
    async tokenInfo(tokenId, config) {
        if (this.provider === undefined || this.mirror === undefined) {
            return Promise.reject("need to connect a valid provider");
        }
        const tokenInfo = await this.mirror.tokenInfo(tokenId, { ...config });
        return { tokenOwner: tokenInfo[0], level: tokenInfo[1] };
    }
    getUsedPreOrderQuota(account, config) {
        if (this.provider === undefined || this.mirror === undefined) {
            return Promise.reject("need to connect a valid provider");
        }
        return this.mirror.getUsedPreOrderQuota(account, { ...config });
    }
    getAccountQuotaAndSignature(account) {
        if (!account || !preOrderList[account]) {
            Promise.reject("cannot find the details of the given account");
        }
        const quota = ethers_1.BigNumber.from(preOrderList[account].quota);
        const signature = preOrderList[account].signature;
        return Promise.resolve({ quota, signature });
    }
    async preOrder(quantity, account, quota, signature, config) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("needs to connect a valid provider and signer");
        }
        const gas = await this.mirror
            .connect(this.signer)
            .estimateGas.preOrder(quantity, account, quota, signature, { ...config });
        const transaction = await this.mirror
            .connect(this.signer)
            .preOrder(quantity, account, quota, signature, { gasLimit: gas.mul(13).div(10), ...config });
        const receipt = await transaction.wait(this._waitConfirmations);
        const newIds = [];
        if (receipt.events) {
            receipt.events
                .filter(event => event.event === "TokenMinted" && event.args)
                .map(event => {
                const newId = event.args.tokenId;
                newIds.push(newId);
            });
        }
        if (newIds.length != quantity.toNumber()) {
            return Promise.reject("the number of tokens minted does not match the declared quantity");
        }
        return { transactionHash: receipt.transactionHash, newTokenIds: newIds };
    }
    async buy(quality, config = {}) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const gas = await this.mirror.connect(this.signer).estimateGas.buy(quality, { ...config });
        const transaction = await this.mirror
            .connect(this.signer)
            .buy(quality, { gasLimit: gas.mul(13).div(10), ...config });
        const receipt = await transaction.wait(this._waitConfirmations);
        const newIds = [];
        if (receipt.events) {
            receipt.events
                .filter(event => event.event === "TokenMinted" && event.args)
                .map(event => {
                const newId = event.args.tokenId;
                newIds.push(newId);
            });
        }
        return { transactionHash: receipt.transactionHash, newTokenIds: newIds };
    }
    async batchAirdrop(receivers, quantities, config) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const gas = await this.mirror.connect(this.signer).estimateGas.batchAirdrop(receivers, quantities, { ...config });
        const transaction = await this.mirror
            .connect(this.signer)
            .batchAirdrop(receivers, quantities, { gasLimit: gas.mul(13).div(10), ...config });
        const receipt = await transaction.wait(this._waitConfirmations);
        const receiverTokenIds = new Map();
        if (receipt.events) {
            receipt.events
                .filter(event => event.event === "TokenMinted" && event.args)
                .map(event => {
                const receiver = event.args.payer;
                const newId = event.args.tokenId;
                let tokens = receiverTokenIds.get(receiver);
                tokens = !tokens ? [] : tokens;
                tokens.push(newId);
                receiverTokenIds.set(receiver, tokens);
            });
        }
        return { transactionHash: receipt.transactionHash, receiverTokenIds };
    }
    async levelUp(tokenId, level, signature, config) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const gas = await this.mirror.connect(this.signer).estimateGas.levelUp(tokenId, level, signature, { ...config });
        const transaction = await this.mirror
            .connect(this.signer)
            .levelUp(tokenId, level, signature, { gasLimit: gas.mul(13).div(10), ...config });
        const receipt = await transaction.wait(this._waitConfirmations);
        return { transactionHash: receipt.transactionHash, tokenId, level };
    }
    async queryTokenMintedEvent(fromBlock, toBlock) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const filteredEvents = await this.mirror.queryFilter(this.mirror.filters.TokenMinted(), fromBlock, toBlock);
        const promises = filteredEvents.map(rawEvent => ({
            payer: rawEvent.args.payer,
            tokenId: rawEvent.args.tokenId.toNumber(),
            eventTime: new Date(rawEvent.args.eventTime.toNumber() * 1000)
        }));
        return await Promise.all(promises);
    }
    async queryLevelUpEvent(fromBlock, toBlock) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const filteredEvents = await this.mirror.queryFilter(this.mirror.filters.TokenLevelUp(), fromBlock, toBlock);
        const promises = filteredEvents.map(rawEvent => ({
            tokenId: rawEvent.args.tokenId.toNumber(),
            level: rawEvent.args.level.toNumber(),
            eventTime: new Date(rawEvent.args.eventTime.toNumber() * 1000)
        }));
        return await Promise.all(promises);
    }
    async queryTransferEvent(fromBlock, toBlock) {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        const filteredEvents = await this.mirror.queryFilter(this.mirror.filters.Transfer(), fromBlock, toBlock);
        const promises = filteredEvents.map(async (rawEvent) => ({
            from: rawEvent.args.from,
            to: rawEvent.args.to,
            tokenId: rawEvent.args.tokenId.toNumber(),
            eventTime: new Date((await rawEvent.getBlock()).timestamp * 1000)
        }));
        return await Promise.all(promises);
    }
    async signPreOrderMessage(privateKey, account, quota) {
        const hash = ethers_1.ethers.utils.arrayify(ethers_1.ethers.utils.solidityKeccak256(["address", "uint256"], [account, quota]));
        const signer = new ethers_1.ethers.Wallet(privateKey);
        const signature = signer.signMessage(hash);
        return signature;
    }
    async signLevelUpMessage(privateKey, tokenId, level) {
        const hash = ethers_1.ethers.utils.arrayify(ethers_1.ethers.utils.solidityKeccak256(["uint256", "uint256"], [tokenId, level]));
        const signer = new ethers_1.ethers.Wallet(privateKey);
        const signature = signer.signMessage(hash);
        return signature;
    }
    getBlockNumber() {
        if (this.provider === undefined || this.mirror === undefined || this.signer === undefined) {
            return Promise.reject("need to connect a valid provider and signer");
        }
        return this.provider.getBlockNumber();
    }
}
exports.EtherMirrorClient = EtherMirrorClient;
//# sourceMappingURL=EtherMirrorClient.js.map